import React, { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui';
import { useNotification } from '../../../contexts/NotificationContext';
import { fetchNextActions, useAiAssistantEnabled } from '../../ai/useAiAssistant';
import type { AiNextAction } from '../../ai/types';
import {
  emptyStateIconClass,
  skeletonPulseClass,
  widgetCardClass,
  widgetTitleClass,
} from '../themeHelpers';
import type { ThemeColors } from '../../../contexts/ThemeContext';

export interface AiNextActionsWidgetProps {
  embedded?: boolean;
}

function priorityDotClass(colors: ThemeColors, priority: AiNextAction['priority']) {
  if (priority === 'high') return colors.error;
  if (priority === 'low') return colors.surfaceHover;
  return colors.warning;
}

function actionHref(action: AiNextAction): string | undefined {
  if (action.route) return action.route;
  if (action.clientId) return `/clients/${action.clientId}/summary`;
  if (action.module === 'todos') return '/todos';
  if (action.module === 'appointments') return '/appointments';
  if (action.module === 'emails') return '/emails';
  return undefined;
}

const AiNextActionsWidget: React.FC<AiNextActionsWidgetProps> = ({ embedded }) => {
  const { colors } = useTheme();
  const { showError } = useNotification();
  const enabled = useAiAssistantEnabled();
  const [actions, setActions] = useState<AiNextAction[]>([]);
  const [loading, setLoading] = useState(false);
  const cardClass = widgetCardClass(colors, embedded);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setActions(await fetchNextActions());
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      showError(message || 'Could not load AI suggestions');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  if (!enabled) {
    return (
      <div className={cardClass}>
        <h2 className={widgetTitleClass(colors)}>AI next actions</h2>
        <p className={cn('text-sm', colors.textSecondary)}>
          Enable AI assistant in Configuration → Application settings.
        </p>
      </div>
    );
  }

  return (
    <div className={cardClass}>
      <div className="flex items-center justify-between gap-2 mb-3">
        <h2 className={widgetTitleClass(colors)}>AI next actions</h2>
        <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
          {loading ? 'Thinking…' : actions.length ? 'Refresh' : 'Suggest'}
        </Button>
      </div>

      {loading && actions.length === 0 ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className={cn('h-12 rounded-lg', skeletonPulseClass(colors))} />
          ))}
        </div>
      ) : null}

      {!loading && actions.length === 0 ? (
        <div className={cn('text-center py-6', colors.textSecondary)}>
          <svg
            className={emptyStateIconClass(colors)}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z"
            />
          </svg>
          <p className="text-sm mt-2">Get prioritized suggestions from your todos, appointments, and inbox.</p>
        </div>
      ) : null}

      {actions.length > 0 ? (
        <ul className="space-y-2">
          {actions.map((action, i) => {
            const href = actionHref(action);
            const inner = (
              <>
                <span
                  className={cn(
                    'mt-1.5 h-2 w-2 shrink-0 rounded-full',
                    priorityDotClass(colors, action.priority),
                  )}
                  aria-hidden
                />
                <div className="min-w-0 flex-1">
                  <p className={cn('text-sm font-medium', colors.text)}>{action.title}</p>
                  <p className={cn('text-xs mt-0.5', colors.textSecondary)}>{action.description}</p>
                </div>
              </>
            );
            return (
              <li key={`${action.title}-${i}`}>
                {href ? (
                  <Link
                    to={href}
                    className={cn(
                      'flex gap-2.5 rounded-lg border px-3 py-2.5 transition-colors hover:opacity-90',
                      colors.border,
                      colors.surfaceHover,
                    )}
                  >
                    {inner}
                  </Link>
                ) : (
                  <div className={cn('flex gap-2.5 rounded-lg border px-3 py-2.5', colors.border, colors.background)}>
                    {inner}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
};

export default AiNextActionsWidget;
